import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@components/common/Card';
import { useFilteredDocuments } from '@api/hooks/useWorkflowStatus';
import { LoadingSpinner } from '@components/common/LoadingSpinner';
import { ConfidenceScore } from '@components/common/ConfidenceScore';
import { formatDate } from '@utils/formatters';
import { ArrowRight } from 'lucide-react';

const documentTypeLabel = (type: string) => {
  switch (type) {
    case 'purchase_order':
      return 'PO';
    case 'receipt':
      return 'Receipt';
    case 'invoice':
      return 'Invoice';
    default:
      return type.replace(/_/g, ' ');
  }
};

export const PendingItemsSummary = () => {
  const navigate = useNavigate();
  const { data: pendingReview, isLoading } = useFilteredDocuments({
    extraction_status: 'pending_review',
    skip: 0,
    limit: 5,
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <LoadingSpinner />
        </CardContent>
      </Card>
    );
  }

  const pendingItems = pendingReview?.documents ?? [];
  const pendingTotal = pendingReview?.total ?? 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle>Pending Review</CardTitle>
          <span className="rounded-full bg-yellow-100 px-3 py-1 text-sm font-semibold text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
            {pendingTotal}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        {pendingItems.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400">No documents need review</p>
        ) : (
          <div className="space-y-3">
            {pendingItems.map((item) => {
              const documentNumber =
                item.external_number || item.po_number || (item.id ? `#${item.id.slice(0, 8)}` : 'Unnumbered');

              return (
                <div
                  key={`${item.type}-${item.id ?? documentNumber}`}
                  className="flex cursor-pointer items-center justify-between rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700"
                  onClick={() =>
                    navigate(`/approvals&document_type=${item.type}`)
                  }
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-gray-900 dark:text-gray-50">
                      {documentTypeLabel(item.type)}: {documentNumber}
                    </p>
                    <p className="truncate text-sm text-gray-500 dark:text-gray-400">
                      {item.vendor_name || 'Unknown vendor'} - {formatDate(item.created_at)}
                    </p>
                  </div>
                  <div className="ml-3 flex items-center gap-3">
                    <ConfidenceScore score={item.confidence} size="sm" />
                    <ArrowRight className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              );
            })}
            <button
              onClick={() => navigate('/approvals')}
              className="w-full mt-3 py-2 text-center text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
            >
              View All Pending ({pendingTotal})
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
